import {
  ApiError,
  type Coupon,
  type CouponDiscountType,
  createCouponSchema,
  formatMoney,
  queryKeys,
} from '@mevaser/shared'
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, TicketPercent } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { InfiniteScrollFooter } from '../components/data-table.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

/** "15%" or "$5.00" — what a code takes off. */
function discountLabel(coupon: Coupon): string {
  if (coupon.discountType === 'percent') {
    return `${(coupon.percentBps ?? 0) / 100}% off`
  }
  return `${formatMoney(coupon.amountCents ?? 0)} off`
}

/** A date picked in the form, taken to mean the end of that local day. */
function endOfDayIso(date: string): string | null {
  if (!date) return null
  const [y, m, d] = date.split('-').map(Number)
  if (!y || !m || !d) return null
  return new Date(y, m - 1, d, 23, 59, 59).toISOString()
}

function toDateInput(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** Blank means "no limit"; anything else must be a whole number. */
function parseLimit(text: string): number | null {
  const trimmed = text.trim()
  if (!trimmed) return null
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : Number.NaN
}

function ExpiryCell({ coupon }: { coupon: Coupon }) {
  if (!coupon.expiresAt) return <span className="text-foreground-muted">Never</span>
  const expired = new Date(coupon.expiresAt).getTime() <= Date.now()
  return (
    <span className="inline-flex items-center gap-2">
      <span className={expired ? 'text-foreground-muted' : undefined}>
        {new Date(coupon.expiresAt).toLocaleDateString()}
      </span>
      {expired ? <Badge tone="neutral">Expired</Badge> : null}
    </span>
  )
}

function UsesCell({ coupon }: { coupon: Coupon }) {
  return (
    <span className="tabular">
      {coupon.uses}
      {coupon.maxUses !== null ? ` / ${coupon.maxUses}` : ''}
      {coupon.perCustomerLimit !== null ? (
        <span className="text-foreground-muted"> · {coupon.perCustomerLimit} per customer</span>
      ) : null}
    </span>
  )
}

export function CouponsPage() {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<Coupon | null>(null)
  const canWrite = can('catalog:write')

  const filters = { q: search || undefined, pageSize: 50 }
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: queryKeys.coupons.list(filters),
    queryFn: ({ pageParam }) =>
      api.get<{ items: Coupon[]; total: number; page: number; pageSize: number }>('/coupons', {
        query: { ...filters, page: pageParam },
      }),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
  })
  const items = data?.pages.flatMap((p) => p.items) ?? []
  const total = data?.pages[0]?.total ?? 0

  const toggleActive = useMutation({
    mutationFn: (input: { id: string; isActive: boolean }) =>
      api.patch(`/coupons/${input.id}`, { isActive: input.isActive }),
    onError: (error) =>
      toast.error('Could not update', error instanceof ApiError ? error.message : undefined),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.coupons.all() }),
  })

  const heading =
    'px-3 py-2 text-start text-2xs font-medium uppercase tracking-wide text-foreground-muted'

  return (
    <Page>
      <PageHeader
        title="Coupons"
        description="Promo codes customers enter at web checkout or on the cabinet tablet — a percentage or dollar amount off the items in their basket."
        actions={
          canWrite ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus /> New coupon
            </Button>
          ) : null
        }
      />

      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-foreground-muted" />
        <Input
          className="ps-8"
          placeholder="Search codes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-surface">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-sunken/60">
                <th className={heading}>Code</th>
                <th className={heading}>Discount</th>
                <th className={heading}>Expires</th>
                <th className={heading}>Used</th>
                <th className={heading}>Active</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {isLoading ? null : items.length > 0 ? (
                items.map((coupon) => (
                  <tr key={coupon.id} className="border-b border-border last:border-0">
                    <td className="px-3 py-2">
                      <div className="font-mono font-medium">{coupon.code}</div>
                      {coupon.note ? (
                        <div className="text-xs text-foreground-muted">{coupon.note}</div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">{discountLabel(coupon)}</td>
                    <td className="px-3 py-2">
                      <ExpiryCell coupon={coupon} />
                    </td>
                    <td className="px-3 py-2">
                      <UsesCell coupon={coupon} />
                    </td>
                    <td className="px-3 py-2">
                      <Switch
                        checked={coupon.isActive}
                        disabled={!canWrite}
                        onCheckedChange={(checked) =>
                          toggleActive.mutate({ id: coupon.id, isActive: checked })
                        }
                        aria-label={`${coupon.isActive ? 'Disable' : 'Enable'} ${coupon.code}`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      {canWrite ? (
                        <Button variant="secondary" size="sm" onClick={() => setEditing(coupon)}>
                          Edit
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6}>
                    <EmptyState
                      icon={TicketPercent}
                      title={search ? 'No matching coupons' : 'No coupons yet'}
                      description={
                        search
                          ? 'Try a different search.'
                          : 'Create a promo code for customers to enter at checkout.'
                      }
                      variant={search ? 'no-results' : undefined}
                      onClearFilters={search ? () => setSearch('') : undefined}
                      action={
                        !search && canWrite
                          ? { label: 'Create a coupon', onClick: () => setCreating(true) }
                          : undefined
                      }
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <InfiniteScrollFooter
          total={total}
          loaded={items.length}
          hasMore={Boolean(hasNextPage)}
          isFetchingMore={isFetchingNextPage}
          onLoadMore={() => void fetchNextPage()}
        />
      </div>

      <CreateCouponDialog open={creating} onOpenChange={setCreating} />
      <EditCouponDialog coupon={editing} onClose={() => setEditing(null)} />
    </Page>
  )
}

function CreateCouponDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [code, setCode] = useState('')
  const [discountType, setDiscountType] = useState<CouponDiscountType>('percent')
  const [amount, setAmount] = useState('')
  const [expires, setExpires] = useState('')
  const [maxUses, setMaxUses] = useState('')
  const [perCustomer, setPerCustomer] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) return
    setCode('')
    setDiscountType('percent')
    setAmount('')
    setExpires('')
    setMaxUses('')
    setPerCustomer('')
    setNote('')
    setError(null)
  }, [open])

  const create = useMutation({
    mutationFn: async () => {
      // The one place the human units are converted: whole or fractional
      // percent to basis points, dollars to cents.
      const entered = Number(amount)
      const scaled = Number.isFinite(entered) && amount.trim() ? Math.round(entered * 100) : null
      const parsed = createCouponSchema.safeParse({
        code,
        discountType,
        percentBps: discountType === 'percent' ? scaled : null,
        amountCents: discountType === 'fixed' ? scaled : null,
        expiresAt: endOfDayIso(expires),
        maxUses: parseLimit(maxUses),
        perCustomerLimit: parseLimit(perCustomer),
        note: note.trim() || null,
      })
      if (!parsed.success) {
        throw new Error(parsed.error.issues[0]?.message ?? 'Check the coupon details')
      }
      return api.post('/coupons', parsed.data)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.coupons.all() })
      toast.success('Coupon created')
      onOpenChange(false)
    },
    onError: (e: Error) => setError(e instanceof ApiError ? e.message : e.message),
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New coupon</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field
            label="Code"
            hint="What customers type at checkout. Letters, numbers, dashes — not case-sensitive."
            required
          >
            {(props) => (
              <Input
                {...props}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="SPRING15"
                autoCapitalize="characters"
              />
            )}
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Type" required>
              {(props) => (
                <Select
                  value={discountType}
                  onValueChange={(v) => setDiscountType(v as CouponDiscountType)}
                >
                  <SelectTrigger {...props}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percent off</SelectItem>
                    <SelectItem value="fixed">Dollar amount off</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label={discountType === 'percent' ? 'Percent off' : 'Dollars off'} required>
              {(props) => (
                <Input
                  {...props}
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={discountType === 'percent' ? '15' : '5.00'}
                />
              )}
            </Field>
          </div>

          <Field
            label="Expires"
            hint="Good through the end of this day. Leave blank for no expiry."
          >
            {(props) => (
              <Input
                {...props}
                type="date"
                value={expires}
                onChange={(e) => setExpires(e.target.value)}
              />
            )}
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Max total uses" hint="Blank = unlimited.">
              {(props) => (
                <Input
                  {...props}
                  inputMode="numeric"
                  value={maxUses}
                  onChange={(e) => setMaxUses(e.target.value)}
                />
              )}
            </Field>
            <Field label="Uses per customer" hint="Blank = unlimited.">
              {(props) => (
                <Input
                  {...props}
                  inputMode="numeric"
                  value={perCustomer}
                  onChange={(e) => setPerCustomer(e.target.value)}
                />
              )}
            </Field>
          </div>

          <Field label="Note" hint="Only staff see this.">
            {(props) => (
              <Textarea
                {...props}
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            )}
          </Field>

          {error ? <p className="text-sm text-danger">{error}</p> : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setError(null)
              create.mutate()
            }}
            disabled={create.isPending}
          >
            {create.isPending ? 'Creating…' : 'Create coupon'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function EditCouponDialog({ coupon, onClose }: { coupon: Coupon | null; onClose: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [expires, setExpires] = useState('')
  const [maxUses, setMaxUses] = useState('')
  const [perCustomer, setPerCustomer] = useState('')
  const [note, setNote] = useState('')

  useEffect(() => {
    if (!coupon) return
    setExpires(toDateInput(coupon.expiresAt))
    setMaxUses(coupon.maxUses === null ? '' : String(coupon.maxUses))
    setPerCustomer(coupon.perCustomerLimit === null ? '' : String(coupon.perCustomerLimit))
    setNote(coupon.note ?? '')
  }, [coupon])

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/coupons/${coupon?.id}`, {
        // Unchanged dates are left as they were rather than re-snapped to
        // end of day.
        expiresAt:
          expires === toDateInput(coupon?.expiresAt ?? null)
            ? (coupon?.expiresAt ?? null)
            : endOfDayIso(expires),
        maxUses: parseLimit(maxUses),
        perCustomerLimit: parseLimit(perCustomer),
        note: note.trim() || null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.coupons.all() })
      toast.success('Saved')
      onClose()
    },
    onError: (error: Error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={coupon !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {coupon?.code}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <p className="text-sm text-foreground-muted">
            {coupon ? discountLabel(coupon) : ''} — the deal itself can't change once a code exists.
            Create a new code to offer something different.
          </p>

          <Field
            label="Expires"
            hint="Good through the end of this day. Leave blank for no expiry."
          >
            {(props) => (
              <Input
                {...props}
                type="date"
                value={expires}
                onChange={(e) => setExpires(e.target.value)}
              />
            )}
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Max total uses" hint="Blank = unlimited.">
              {(props) => (
                <Input
                  {...props}
                  inputMode="numeric"
                  value={maxUses}
                  onChange={(e) => setMaxUses(e.target.value)}
                />
              )}
            </Field>
            <Field label="Uses per customer" hint="Blank = unlimited.">
              {(props) => (
                <Input
                  {...props}
                  inputMode="numeric"
                  value={perCustomer}
                  onChange={(e) => setPerCustomer(e.target.value)}
                />
              )}
            </Field>
          </div>

          <Field label="Note" hint="Only staff see this.">
            {(props) => (
              <Textarea
                {...props}
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
